import { COMPANY_UID_INPUT_REGEX, type OnboardingValidationDocument } from '@houzy/schemas/partner-cockpit/onboarding';
import {
    MAX_VALIDATION_DOCUMENTS_PER_TYPE,
    VALIDATION_DOCUMENT_MAX_BYTES,
    VALIDATION_DOCUMENT_MAX_SIZE_MB,
    VALIDATION_DOCUMENT_MIME_TYPES,
    ValidationDocumentType,
} from '@houzy/schemas/partner-management/partner-validation';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { DocumentUploader } from '../../../components/DocumentUploader';
import { apiErrorCode } from '../../../lib/api';
import {
    onboardingApplicationQueryOptions,
    useAddValidationDocumentMutation,
    useDeleteValidationDocumentMutation,
    useSaveValidationMutation,
} from '../api/onboardingApi';
import { OnboardingFooter } from '../components/OnboardingFooter';
import { OnboardingStepContent } from '../components/OnboardingStepContent';
import { OnboardingStepError } from '../components/OnboardingStepError';
import { OnboardingStepHeading } from '../components/OnboardingStepHeading';
import { useOnboardingState } from '../state/onboardingContext';
import { getNextStep } from '../steps';

const UID_FIELD_ERROR_KEYS: Record<string, string> = {
    COMPANY_UID_NOT_FOUND: 'ONBOARDING.VALIDATION.UID_NOT_FOUND',
};

export function ValidationStep(): ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { validation, setValidation } = useOnboardingState();
    const { data: application } = useQuery(onboardingApplicationQueryOptions);
    const saveValidation = useSaveValidationMutation();
    const addDocument = useAddValidationDocumentMutation();
    const deleteDocument = useDeleteValidationDocumentMutation();

    const prefilled = useRef(false);
    const savedUid = application?.validation?.uid;
    useEffect(() => {
        if (prefilled.current || !savedUid) {
            return;
        }
        prefilled.current = true;
        const savedDigits = savedUid.replace(/^CHE-/i, '');
        setValidation((prev) => (prev.uid ? prev : { ...prev, uid: savedDigits }));
    }, [savedUid, setValidation]);

    const documents = application?.validation?.documents ?? [];
    const documentsOfType = (type: ValidationDocumentType): ReadonlyArray<OnboardingValidationDocument> =>
        documents.filter((document) => document.type === type);

    const uploadDocument = async (type: ValidationDocumentType, file: File): Promise<void> => {
        await addDocument.mutateAsync({ type, file });
    };

    const trimmedUid = validation.uid.trim();
    const uidFormatInvalid = trimmedUid.length > 0 && !COMPANY_UID_INPUT_REGEX.test(trimmedUid);

    const errorCode = apiErrorCode(saveValidation.error);
    const uidServerErrorKey = errorCode ? UID_FIELD_ERROR_KEYS[errorCode] : undefined;
    const uidErrorKey = uidFormatInvalid ? 'ONBOARDING.VALIDATION.UID_FORMAT_ERROR' : uidServerErrorKey;

    const hasSomethingToValidate = (trimmedUid.length > 0 && !uidFormatInvalid) || documents.length > 0;

    const setUid = (uid: string): void => {
        const sanitized = uid.replace(/[^\d.]/g, '');
        if (sanitized.replace(/\D/g, '').length > 9) {
            return;
        }
        setValidation((prev) => ({ ...prev, uid: sanitized }));
        if (saveValidation.isError) {
            saveValidation.reset();
        }
    };

    const persistAndContinue = (uid: string | undefined): void => {
        saveValidation.mutate(
            { uid },
            {
                onSuccess: () => {
                    const next = getNextStep('validation');
                    if (next) {
                        void navigate({ to: next.path });
                    }
                },
            },
        );
    };

    const handleNext = (): void => {
        if (uidFormatInvalid || saveValidation.isPending) {
            return;
        }
        persistAndContinue(trimmedUid.length > 0 ? trimmedUid : undefined);
    };

    const handleSkip = (): void => {
        if (saveValidation.isPending) {
            return;
        }
        persistAndContinue(undefined);
    };

    return (
        <>
            <OnboardingStepContent>
                <Stack spacing={6}>
                    <OnboardingStepHeading
                        title={t('ONBOARDING.VALIDATION.TITLE')}
                        description={t('ONBOARDING.VALIDATION.DESCRIPTION')}
                    />

                    <Stack spacing={3}>
                        <Alert severity="info">
                            <Trans i18nKey="ONBOARDING.VALIDATION.ALERT" components={{ strong: <strong /> }} />
                        </Alert>

                        {!uidServerErrorKey && <OnboardingStepError error={saveValidation.error} />}

                        <TextField
                            label={t('ONBOARDING.VALIDATION.UID_LABEL')}
                            placeholder="123.456.789"
                            fullWidth
                            value={validation.uid}
                            onChange={(e) => setUid(e.target.value)}
                            error={Boolean(uidErrorKey)}
                            helperText={
                                uidErrorKey ? (
                                    t(uidErrorKey)
                                ) : (
                                    <Trans
                                        i18nKey="ONBOARDING.VALIDATION.UID_HELPER"
                                        components={{
                                            uidLink: (
                                                <Link
                                                    href={t('ONBOARDING.VALIDATION.UID_REGISTER_URL')}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    underline="always"
                                                />
                                            ),
                                        }}
                                    />
                                )
                            }
                            slotProps={{
                                formHelperText: { component: 'div' },
                                input: {
                                    startAdornment: <InputAdornment position="start">CHE-</InputAdornment>,
                                },
                            }}
                        />

                        <DocumentUploader
                            label={t('ONBOARDING.VALIDATION.DOCS.COMPANY_INSURANCE')}
                            description={t('ONBOARDING.VALIDATION.DOCS.COMPANY_INSURANCE_DESCRIPTION')}
                            acceptedMimeTypes={VALIDATION_DOCUMENT_MIME_TYPES.CompanyAssuranceDocument}
                            maxSizeBytes={VALIDATION_DOCUMENT_MAX_BYTES}
                            maxCount={MAX_VALIDATION_DOCUMENTS_PER_TYPE.CompanyAssuranceDocument}
                            hint={t('ONBOARDING.VALIDATION.DOCS.HINT_IMAGE_PDF', {
                                mb: VALIDATION_DOCUMENT_MAX_SIZE_MB,
                            })}
                            documents={documentsOfType(ValidationDocumentType.CompanyAssuranceDocument)}
                            onUpload={(file) => uploadDocument(ValidationDocumentType.CompanyAssuranceDocument, file)}
                            onRemove={(uuid) => deleteDocument.mutateAsync(uuid).then(() => undefined)}
                        />

                        <Stack spacing={1}>
                            <DocumentUploader
                                label={t('ONBOARDING.VALIDATION.DOCS.ID_CARD')}
                                description={t('ONBOARDING.VALIDATION.DOCS.ID_CARD_DESCRIPTION')}
                                acceptedMimeTypes={VALIDATION_DOCUMENT_MIME_TYPES.IdentityDocument}
                                maxSizeBytes={VALIDATION_DOCUMENT_MAX_BYTES}
                                maxCount={MAX_VALIDATION_DOCUMENTS_PER_TYPE.IdentityDocument}
                                hint={t('ONBOARDING.VALIDATION.DOCS.HINT_IMAGE_PDF', {
                                    mb: VALIDATION_DOCUMENT_MAX_SIZE_MB,
                                })}
                                documents={documentsOfType(ValidationDocumentType.IdentityDocument)}
                                onUpload={(file) => uploadDocument(ValidationDocumentType.IdentityDocument, file)}
                                onRemove={(uuid) => deleteDocument.mutateAsync(uuid).then(() => undefined)}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {t('ONBOARDING.VALIDATION.DOCS.ID_PRIVACY_NOTICE')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </OnboardingStepContent>

            <OnboardingFooter
                currentStep="validation"
                nextDisabled={!hasSomethingToValidate || saveValidation.isPending}
                onNext={handleNext}
                onSkip={handleSkip}
            />
        </>
    );
}
